<template>
  <layout width="428">
    <template slot="header"> {{ isNew ? 'Add new' : 'Edit' }} media </template>
    <validation-observer ref="form">
      <v-form ref="form" class="mx-4" :disabled="isLoading">
        <validation-provider v-slot="{ errors }" name="Name" rules="required">
          <v-text-field
            data-test-type="deviceName"
            class="mt-2 mb-2"
            v-model.trim="name"
            label="Name"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <div>
          <template>
            <validation-provider
              v-if="isNew"
              v-slot="{ errors }"
              name="Schema"
              rules="required"
            >
              <v-autocomplete
                clearable
                data-test-schema="deviceSchema"
                v-model="schemaId"
                :items="schemas"
                item-text="name"
                item-value="id"
                label="Schema"
                class="ma-0"
                :error-messages="errors"
                :disabled="isLoadSchemas"
                :loading="isLoadSchemas"
              >
                <template v-slot:item="item">
                  <div :data-test-schema="item.item.name">
                    {{ item.item.name }}
                  </div>
                </template>
              </v-autocomplete>
            </validation-provider>
            <div
              class="d-flex align-center mx-0"
              v-if="isNew"
              data-test="enabled"
            >
              <div style="font-size: 16px">Enabled</div>
              <v-spacer />
              <v-switch
                data-test-switch="enabled"
                class="ma-0 py-3"
                hide-details
                :false-value="false"
                :true-value="true"
                v-model="enabled"
              ></v-switch>
            </div>
            <media-input
              v-model="formData"
              :id="entity.id"
              :loading="isMediaUploading"
              rules="size:20"
              :media-type="mediaType"
            />
            <subheader>Description</subheader>
            <v-textarea
              data-test="description"
              v-model="description"
              no-resize
              rows="1"
              auto-grow
            ></v-textarea>
          </template>
        </div>
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="deviceCloseAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="deviceCreateOrEditAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

setInteractionMode('eager');

import { required } from 'vee-validate/dist/rules';
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from 'vee-validate';

extend('required', {
  ...required,
  message: '{_field_} can not be empty'
});

import useAuth from '@/api/auth';
import { useLazyQuery, useMutation, useResult } from '@vue/apollo-composable';

import schema from '@/api/schemas/list.graphql';
import schemaDevice from '@/api/devices/list.graphql';
import objectItem from '@/api/objects/item.graphql';
import axios from 'axios';
import MediaInput from '@/components/MediaInput';
import useMedia from '@/compositions/useMedia';

export default {
  name: 'CreateMedia',
  props: {
    entity: {
      type: [Object],
      required: true
    },
    entityType: {
      type: String,
      default: ''
    },
    linkAfterCreation: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    callbackAfter: {
      type: Function,
      default: () => {}
    },
    mediaType: {
      type: String,
      default: ''
    }
  },
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider,
    MediaInput
  },

  setup(props, { emit, root, refs }) {
    const controller = new window.AbortController();
    const { getImageById, uploadMedia } = useMedia();
    const { user } = new useAuth();
    const picture = ref(null);
    const isNonImage = ref(false);
    const isLoading = ref(false);
    const formData = ref(null);
    const isMediaUploading = ref(false);
    const options = ref({
      bgColor: 'transparent',
      textColor: 'white',
      showTitle: false,
      shadow: true
    });

    const localEntity = reactive({
      id: props.entity.id,
      schemaId: props.entity.schemaId,
      name: props.entity.name,
      enabled: props.entity.enabled,
      description: props.entity.description
    });

    const selectedType = ref('MEDIA');

    const { mutate: linkObjToObj } = useMutation(
      objectItem.connectObjectsToObject
    );

    const { mutate: createObject, loading: isCreatingObject } = useMutation(
      schemaDevice.save,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const { mutate: updateObject, loading: isUpdatingObject } = useMutation(
      objectItem.update,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const lazyQuery = useLazyQuery(
      schema.loadPure,
      {
        filter: {
          type: {
            equalTo: selectedType
          }
        }
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const schemas = useResult(lazyQuery.result, [], data => {
      return data.schemata;
    });

    const isNew = computed(() => {
      return !localEntity.id;
    });

    function close(e) {
      if (isCreatingObject.value || isUpdatingObject.value) {
        controller.abort();
      }

      emit('close', e);
    }

    const createObjectHandler = () => {
      createObject({
        input: {
          object: {
            schemaId: localEntity.schemaId,
            name: localEntity.name,
            enabled: localEntity.enabled,
            description: localEntity.description,
            editorgroup: user.value.defaultEditorgroup
          }
        }
      })
        .then(({ data }) => {
          isMediaUploading.value = true;
          let promise = Promise.resolve(data);

          if (formData.value) {
            promise = uploadMedia(
              formData.value,
              null,
              data.createObject.object.id
            );
          }

          return promise;
        })
        .then(data => {
          const id = data?.createObject?.object?.id || data.data;
          const entityId = root.$route.query.nestedId || root.$route.params.id;
          props.callbackAfter();
          if (props.linkAfterCreation) {
            linkObjToObj({
              input: {
                objectsToObject: {
                  object1Id: entityId,
                  object2Id: id
                }
              }
            }).then(() => {
              close(id);
            });
          } else {
            close(id);
            redirectToCard(id);
          }
        })
        .finally(() => {
          isMediaUploading.value = false;
        });
    };

    const updateObjectHandler = () => {
      updateObject({
        input: {
          id: props.entity.id,
          patch: {
            name: localEntity.name,
            enabled: localEntity.enabled,
            description: localEntity.description
          }
        }
      })
        .then(({ data }) => {
          isMediaUploading.value = true;
          let promise = Promise.resolve(data);

          if (formData.value) {
            promise = uploadMedia(formData.value, null, props.entity.id);
          }

          return promise;
        })
        .then(() => {
          close();
        })
        .finally(() => {
          isMediaUploading.value = false;
        });
    };

    const redirectToCard = objectId => {
      let routeName = 'mediasTableComponent';

      root.$router.push({
        name: routeName,
        params: { id: objectId }
      });
    };

    onMounted(() => {
      lazyQuery.load();

      if (props.entity.id) {
        picture.value = getImageById(props.entity.id);
      } else {
        localEntity.enabled = true;
      }
    });

    const handleSave = () => {
      refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    return {
      ...toRefs(localEntity),
      selectedType,
      isNew,
      close,
      schemas,
      handleSave,
      isLoading: isCreatingObject || isUpdatingObject || isMediaUploading,
      isMediaUploading,
      isLoadSchemas: lazyQuery.loading,
      picture,
      isNonImage,
      formData,
      options
    };
  }
};
</script>

<style lang="scss" scoped>
.user_photo {
  position: relative;
  background-color: #dbdbdb;
  background-position: center;
  min-height: 120px;
}
.upload {
  position: absolute;
  top: 15px;
  right: 15px;
}

.shadow {
  background-color: rgba(0, 0, 0, 0.44) !important;
  border-color: rgba(0, 0, 0, 0.44) !important;
}
</style>
